import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Draggable, InertiaPlugin } from "gsap/all";

gsap.registerPlugin(Draggable, InertiaPlugin, ScrollTrigger);

const GsapCarousel = (() => {
    "use strict";
    // Utility method to extend defaults with user options
    function extendDefaults(source, properties) {
        let property;
        for (property in properties) {
            if (properties.hasOwnProperty(property)) {
                source[property] = properties[property];
            }
        }
        return source;
    }

    const init = (config) => {
        let next;
        let prev;
        let slides;
        let container;
        let inner;
        let nav;
        // Define option defaults
        let options = null;
        let defaults = {
            el: null,
            slideshowCls: "gsap-slideshow",
            slideshowContainerCls: "gsap-slideshow-container",
            slideCls: "gsap-slide",
            slideInner: "gsap-slideshow-inner",
            slidesSelector: "[data-testimonial]",
            containerSelector: "[data-testimonial-container]",
            innerSelector: "[data-testimonial-inner]",
            slideDelay: 10,
            rotate: true,
            autoPause: true,
            duration: 0.25,
            bleed: false, // when set to true, will automatically resize width to assure items bleed off container
            dotNav: true,
            nextButton: false,
            prevButton: false,
            autoContainerHeight: false,
            navGroup: false, // groups visible so that next/prev advances by that amount
            debug: false,
            heightPadding: 30,
            overflow: "hidden",
            equalizeHeight: false
        };

        // Create options by extending defaults with the passed in arugments
        if (config && typeof config === "object") {
            options = extendDefaults(defaults, config);
        } else {
            options = defaults;
        }

        // Grab the elems we need
        if (options.debug) {
            console.log(options.el);
            console.log(options.slidesSelector);
        }
        slides = options.el.querySelectorAll(options.slidesSelector);
        container = options.el.querySelector(options.containerSelector);
        inner = options.el.querySelector(options.innerSelector);
        let slideCls = options.slideCls;

        if(checkIfNeeded()) {
            // Add classes
            options.el.classList.add(options.slideshowCls);
            container.classList.add(options.slideshowContainerCls);
            inner.classList.add(options.slideInner);
            if (options.overflow === "hidden") {
                container.classList.add(
                    options.slideshowContainerCls + "-overflow-hidden",
                );
                inner.classList.add(options.slideInner + "-overflow-hidden");
            }
            slides.forEach((element) => {
                element.classList.add(slideCls);
            });

            if (options.dotNav) {
                nav = document.createElement("ul");
                nav.classList.add("slides-nav");
                options.el.appendChild(nav);

                slides.forEach((element) => {
                    let li = document.createElement("li");
                    let inner = document.createElement("span");
                    li.appendChild(inner);
                    li.classList.add("slides-nav-item");
                    nav.appendChild(li);
                });
            }

            if (options.bleed) {
                handleBleed();
            }
            if(options.equalizeHeight) {
                handleHeight();
            }
            handleRotator(options);
        } else {
            let nextBtn = options.el.querySelector(options.nextButton);
            let prevBtn = options.el.querySelector(options.prevButton);
            if(nextBtn) {
                nextBtn.classList.add('hidden');
            }
            if(prevBtn) {
                prevBtn.classList.add('hidden');
            }
        }


        function checkIfNeeded() {
            let totalWidth = slides[0].offsetWidth * slides.length;
            if(totalWidth < options.el.offsetWidth) {
                return false
            }
            return true;
        };

        // calculates the native width
        function handleBleed() {
            if (slides.length < 2) {
                return;
            }
            // first remove any inline widths that may be applied to each slide
            slides.forEach((e) => {
                e.style.width = "";
            });

            let singleWidth = slides[1].offsetWidth;
            let tolerance = 0.2;
            let totalWidth = inner.offsetWidth;
            // calc how many full can fit
            let fit = totalWidth / singleWidth;

            // if the difference is less than the tolerance, bleed it
            let offset = Math.abs(1 - (fit - Math.floor(fit)));
            if (offset < tolerance || 1 - offset < tolerance) {
                if (offset < tolerance) {
                    // almost entire card is showing, let's nudge it down
                    let updateWidth = singleWidth * (1 - offset);
                    setSlideWidths(Math.round(updateWidth));
                } else {
                    // only a small amount of next is showing, lets show more
                    let updateWidth = singleWidth * (1 - offset + 1);
                    setSlideWidths(Math.round(updateWidth));
                }
            }
        }

        function calculateSlidesFit() {
            let singleWidth = slides[1].offsetWidth;
            let totalWidth = inner.offsetWidth;
            return Math.abs(totalWidth / singleWidth);
        }

        function setSlideWidths(width) {
            slides.forEach((e) => {
                e.style.width = width + "px";
            });
        }

        function handleHeight() {
            if (slides.length < 2) {
                return;
            }
            if(options.equalizeHeight) {

                let maxHeight = 0;
                slides.forEach((element) => {
                   element.style.height = "";
                   if (element.offsetHeight > maxHeight) {
                       maxHeight = element.offsetHeight;
                   }
                });

                slides.forEach((element) => {
                    element.style.height = maxHeight + 'px';
                });

            }
        }

        //
        function handleRotator(options) {
            if (slides.length < 2) {
                return;
            }
            // NOTE: overwrite: "auto" fixes inertia plugin in combination with prev/next not always returning
            // the expected slide.
            // https://greensock.com/forums/topic/22235-how-to-stop-work-of-inertia-plugin/?do=findComment&comment=105112&_rid=42009
            gsap.defaults({
                overwrite: "auto",
            });

            let indexWrap = gsap.utils.wrap(0, slides.length);
            let timer;
            let numSlides = slides.length;

            // start building the slider
            for (let i = 0; i < numSlides; i++) {
                gsap.set(slides[i], {
                    xPercent: i * 100,
                });
            }
            if (options.rotate === true) {
                if (!options.autoPause) {
                    timer = gsap.delayedCall(options.slideDelay, autoPlay);
                } else {
                    ScrollTrigger.create({
                        trigger: options.el,
                        start: "top center",
                        end: "bottom center",
                        onEnter: (self) => {
                            timer = gsap.delayedCall(
                                options.slideDelay,
                                autoPlay,
                            );
                        },
                        onLeave: (self) => {
                            timer.kill();
                        },
                    });
                }
            }
            // Next and previous buttons if
            // enabled
            if (options.nextButton) {
                let nextBtn = options.el.querySelector(options.nextButton);
                let prevBtn = options.el.querySelector(options.prevButton);
                nextBtn.addEventListener("click", handleDirectionClick);
                prevBtn.addEventListener("click", handleDirectionClick);
            }

            function handleDirectionClick(e) {
                e.preventDefault();
                let direction = "";
                let { currentTarget } = e;
                if (
                    currentTarget ===
                    options.el.querySelector(options.prevButton)
                ) {
                    direction = "backward";
                }
                let advanceAmount = 1;
                if (options.navGroup) {
                    advanceAmount = Math.abs(calculateSlidesFit());
                }
                if (direction === "backward") {
                    advanceCaro(-advanceAmount);
                } else {
                    advanceCaro(advanceAmount);
                }
            }

            function advanceCaro(number) {
                slideAnimation.kill();
                let x = snapX(
                    gsap.getProperty(proxy, "x") - number * slideWidth,
                );
                slideAnimation = gsap.to(proxy, {
                    duration: options.duration,
                    x: x,
                    onUpdate: updateProgress,
                });
            }

            const animation = gsap.to(slides, {
                duration: 1,
                xPercent: "+=" + numSlides * 100,
                ease: "none",
                paused: true,
                repeat: -1,
                modifiers: {
                    xPercent: gsap.utils.wrap(-100, (numSlides - 1) * 100),
                },
            });
            const proxy = document.createElement("div");
            gsap.set(proxy, { x: 0 });
            let slideAnimation = gsap.to({}, { duration: 0.1 });
            let slideWidth = 0;
            let slideHeight = 0;
            let wrapWidth = 0;
            resize();

            let draggable = new Draggable(proxy, {
                throwResistance: 1500,
                maxDuration: 0.8,
                minDuration: 0.2,
                trigger: container,
                throwProps: true,
                onPress: updateDraggable(options),
                onDrag: updateProgress,
                onThrowUpdate: updateProgress,
                snap: {
                    x: gsap.utils.snap(slideWidth),
                },
            });

            window.addEventListener("resize", resize);

            function updateDraggable(options) {
                if (options.rotate) {
                    if (typeof timer !== "undefined") {
                        timer.restart(true);
                    }
                }
                slideAnimation.kill();
                // this.update();
            }

            if (options.dotNav) {
                watchDotNav();
            }

            function watchDotNav() {
                if (!options.dotNav) {
                    return false;
                }
                let items = nav.querySelectorAll("li");
                items.forEach((element) => {
                    element.addEventListener("click", handleDotNavClick);
                });
            }
            function handleDotNavClick(e) {
                let { currentTarget } = e;
                let items = nav.querySelectorAll("li");
                const navIndex = [...items].indexOf(currentTarget);
                const currentIndex = getCurrentIndex();
                // console.log(currentTarget);
                advanceCaro(navIndex - currentIndex);
                // if (options.debug) {
                //     console.log(event.currentTarget);
                // }
            }

            function updateDotNav(index) {
                if (!options.dotNav) {
                    return false;
                }
                let items = nav.querySelectorAll("li");
                if (options.dotNav) {
                    if (options.debug) {
                        console.log("calling this back");
                        console.log(index);
                    }
                    items.forEach((element) => {
                        element.classList.remove("s-active");
                    });
                    items[index].classList.add("s-active");
                }
            }

            function getCurrentIndex() {
                let x = snapX(gsap.getProperty(proxy, "x"));
                return indexWrap(-x / slideWidth);
            }

            function animateSlides(direction) {
                if (options.rotate) {
                    if (typeof timer !== "undefined") {
                        timer.restart(true);
                    }
                }
                slideAnimation.kill();
                let x = snapX(
                    gsap.getProperty(proxy, "x") + direction * slideWidth,
                );
                slideAnimation = gsap.to(proxy, {
                    duration: options.duration,
                    x: x,
                    onUpdate: updateProgress,
                });
                let index = indexWrap(-x / slideWidth);
                updateDotNav(index);
                updateContainerHeight();
            }

            function updateContainerHeight() {
                if (!options.autoContainerHeight) {
                    return false;
                }
                // find the tallest visible slide, and animate height of container
                // based on that
                const currentIndex = getCurrentIndex();
                const fit = calculateSlidesFit();
                let tallest = 0;
                // only find visible elements
                slides.forEach((element, index) => {
                    if (index >= currentIndex && index <= currentIndex + fit) {
                        if (element.offsetHeight > tallest) {
                            tallest = element.offsetHeight;
                        }
                    }
                });

                gsap.fromTo(
                    container,
                    {
                        height: container.offsetHeight,
                    },
                    {
                        height: tallest + options.heightPadding,
                        duration: 0.2,
                        ease: "back.out(1)",
                    },
                );
            }

            function autoPlay() {
                if (
                    draggable.isPressed ||
                    draggable.isDragging ||
                    draggable.isThrowing
                ) {
                    if (otate) {
                        timer.restart(true);
                    }
                } else {
                    animateSlides(-1);
                }
            }

            function updateProgress() {
                animation.progress(
                    gsap.utils.wrap(
                        0,
                        1,
                        gsap.getProperty(proxy, "x") / wrapWidth,
                    ),
                );
                let x = snapX(gsap.getProperty(proxy, "x"));
                let index = indexWrap(-x / slideWidth);
                updateDotNav(index);
                updateContainerHeight();
            }

            function resize() {
                let norm = gsap.getProperty(proxy, "x") / wrapWidth || 0;
                slideWidth = slides[0].offsetWidth;
                slideHeight = 0;
                wrapWidth = slideWidth * numSlides;

                slides.forEach((element) => {
                    if (element.offsetHeight > slideHeight) {
                        slideHeight = element.offsetHeight;
                    }
                });

                container.style.height = slideHeight + "px";

                gsap.set(proxy, {
                    x: norm * wrapWidth,
                });
                animateSlides(0);
                slideAnimation.progress(1);
            }

            function snapX(x) {
                return Math.round(x / slideWidth) * slideWidth;
            }
            // if there are more than one, create rotator
        }
    };

    return { init };
})();

export default GsapCarousel;
window.TestPlugin = GsapCarousel;
